import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';
import ManageHeroSlides from './ManageHeroSlides';

const ICON_OPTIONS = [
  { value: 'medical_services', label: 'Medical Services' },
  { value: 'healing', label: 'Healing / Surgical' },
  { value: 'biotech', label: 'Biotech / Diagnostic' },
  { value: 'emergency', label: 'Emergency' },
];

const EMPTY_SERVICE_FORM = {
  name: '',
  description: '',
  icon: 'medical_services',
  route: '',
  orderIndex: 0,
  isActive: true,
};

const EMPTY_CTA_FORM = {
  homeCtaHeading: '',
  homeCtaSubtitle: '',
  homeCtaButton1Text: '',
  homeCtaButton1Link: '',
  homeCtaButton2Text: '',
  homeCtaButton2Link: '',
};

const ManageHomeContent = () => {
  // Featured services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(EMPTY_SERVICE_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // CTA banner state
  const [ctaForm, setCtaForm] = useState(EMPTY_CTA_FORM);
  const [ctaLoading, setCtaLoading] = useState(true);
  const [ctaSaving, setCtaSaving] = useState(false);
  const [ctaError, setCtaError] = useState('');
  const [ctaSaved, setCtaSaved] = useState(false);

  const loadServices = useCallback(async () => {
    setServicesLoading(true);
    setServicesError('');
    try {
      const result = await apiClient.getFeaturedServices();
      if (result && result.success) {
        setServices(result.data);
      } else {
        setServicesError(result?.error || 'Failed to load services');
      }
    } catch (e) {
      setServicesError(e.message || 'Failed to load services');
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const loadCta = useCallback(async () => {
    setCtaLoading(true);
    setCtaError('');
    try {
      const result = await apiClient.getHospitalInfo();
      if (result && result.success && result.data) {
        setCtaForm({
          homeCtaHeading: result.data.homeCtaHeading || '',
          homeCtaSubtitle: result.data.homeCtaSubtitle || '',
          homeCtaButton1Text: result.data.homeCtaButton1Text || '',
          homeCtaButton1Link: result.data.homeCtaButton1Link || '',
          homeCtaButton2Text: result.data.homeCtaButton2Text || '',
          homeCtaButton2Link: result.data.homeCtaButton2Link || '',
        });
      }
    } catch (e) {
      setCtaError(e.message || 'Failed to load CTA banner content');
    } finally {
      setCtaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
    loadCta();
  }, [loadServices, loadCta]);

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || '',
        description: service.description || '',
        icon: service.icon || 'medical_services',
        route: service.route || '',
        orderIndex: service.orderIndex || 0,
        isActive: service.isActive !== false,
      });
    } else {
      setEditingService(null);
      setFormData({ ...EMPTY_SERVICE_FORM, orderIndex: services.length + 1 });
    }
    setImageFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
    setFormData(EMPTY_SERVICE_FORM);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'orderIndex' ? Number(value) : value,
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile(file);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setServicesError('');
    try {
      let result;
      if (editingService) {
        result = await apiClient.updateFeaturedService(editingService.id, formData, imageFile);
      } else {
        result = await apiClient.addFeaturedService(formData, imageFile);
      }
      if (result && result.success) {
        handleCloseDialog();
        await loadServices();
      } else {
        setServicesError(result?.error || 'Failed to save service');
      }
    } catch (e) {
      setServicesError(e.message || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      const result = await apiClient.deleteFeaturedService(id);
      if (result && result.success) {
        await loadServices();
      } else {
        setServicesError(result?.error || 'Failed to delete service');
      }
    } catch (e) {
      setServicesError(e.message || 'Failed to delete service');
    }
  };

  const handleCtaInputChange = (e) => {
    const { name, value } = e.target;
    setCtaForm((prev) => ({ ...prev, [name]: value }));
    setCtaSaved(false);
  };

  const handleCtaSave = async () => {
    setCtaSaving(true);
    setCtaError('');
    setCtaSaved(false);
    try {
      const result = await apiClient.updateHospitalInfo(ctaForm);
      if (result && result.success) {
        setCtaSaved(true);
      } else {
        setCtaError(result?.error || 'Failed to save CTA banner');
      }
    } catch (e) {
      setCtaError(e.message || 'Failed to save CTA banner');
    } finally {
      setCtaSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Homepage Content
      </Typography>

      {/* Hero Slider */}
      <ManageHeroSlides />

      <Divider sx={{ my: 4 }} />

      {/* Featured Services */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Featured Services</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Service
        </Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are the 4 service cards shown in the "Our Services" section on the homepage.
      </Alert>
      {servicesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServicesError('')}>
          {servicesError}
        </Alert>
      )}
      {servicesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>{service.orderIndex}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {service.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{service.route}</TableCell>
                  <TableCell>{service.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(service)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(service.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No services yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 4 }} />

      {/* CTA Banner */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        "Ready to Get Started?" Banner
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This is the call-to-action section at the bottom of the homepage.
      </Alert>
      {ctaError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCtaError('')}>
          {ctaError}
        </Alert>
      )}
      {ctaSaved && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCtaSaved(false)}>
          Saved successfully.
        </Alert>
      )}
      {ctaLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Heading"
                name="homeCtaHeading"
                value={ctaForm.homeCtaHeading}
                onChange={handleCtaInputChange}
                placeholder="Ready to Get Started?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtitle"
                name="homeCtaSubtitle"
                value={ctaForm.homeCtaSubtitle}
                onChange={handleCtaInputChange}
                multiline
                rows={2}
                placeholder="Schedule an appointment or contact us for more information. Your health journey starts here."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button 1 Text"
                name="homeCtaButton1Text"
                value={ctaForm.homeCtaButton1Text}
                onChange={handleCtaInputChange}
                placeholder="Schedule Appointment"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button 1 Link"
                name="homeCtaButton1Link"
                value={ctaForm.homeCtaButton1Link}
                onChange={handleCtaInputChange}
                placeholder="/contact"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button 2 Text"
                name="homeCtaButton2Text"
                value={ctaForm.homeCtaButton2Text}
                onChange={handleCtaInputChange}
                placeholder="Join Our Team"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button 2 Link"
                name="homeCtaButton2Link"
                value={ctaForm.homeCtaButton2Link}
                onChange={handleCtaInputChange}
                placeholder="/careers"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button variant="contained" onClick={handleCtaSave} disabled={ctaSaving}>
              {ctaSaving ? 'Saving...' : 'Save Banner'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Add/Edit Service Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                name="orderIndex"
                value={formData.orderIndex}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Icon"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
              >
                {ICON_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Link (route)"
                name="route"
                value={formData.route}
                onChange={handleInputChange}
                placeholder="/services/medical-clinics"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label">
                {imageFile ? 'Change Image' : 'Upload Image'}
                <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              </Button>
              {imageFile && (
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {imageFile.name}
                </Typography>
              )}
              {!imageFile && editingService?.image && (
                <Typography variant="caption" sx={{ ml: 2, display: 'block', mt: 1 }} color="text.secondary">
                  Keeping existing image unless a new one is uploaded.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch name="isActive" checked={formData.isActive} onChange={handleSwitchChange} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingService ? 'Update Service' : 'Add Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageHomeContent;
